import React from "react"
import { Group, Radio } from "@/components"
import { enumOrderSort } from "@/helpers/enums"
import classes from "@/pages/campaign-form/components/campaigns/personalizedMessages/Messaging.module.css"
import { TwoColumnsLayout } from "@/pages/campaign-form/components/layouts/TwoColumnsLayout/TwoColumnsLayout"
import { validatePlaceholders, isCustomTargetingEnabled } from "@/pages/campaign-form/helpers"
import { useCampaignPlaceholders } from "@/services/campaigns"
import { useFormContext } from "react-hook-form"
import { CustomQuestions } from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/CustomQuestions/index"
import { useDisclosure } from "@mantine/hooks"
import { CreateQuestionModal } from "./components/CustomQuestions/CreateQuestionModal"
import AddTextItemsToListInput from "@/components/Controlled/AddTextItemsToListInput/AddTextItemsToListInput"
import { emailValidation } from "shared/imports/validationFunctions"
import { SavedQuestionsModal } from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/CustomQuestions/SavedQuestionsModal"
import { TextAreaWithPlaceholders } from "@/components/TextWithPlaceholders/TextAreaWithPlaceholders"
import { TextInputWithPlaceholders } from "@/components/TextWithPlaceholders/TextInputWithPlaceholders"
import { TargetedOfficialsSelectionWriteLetter } from "@/pages/campaign-form/components/sections/message/TargetedOfficialsSelectionWriteLetter/TargetedOfficialsSelectionWriteLetter"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"
import { ReworkWithAIButton } from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/AI/ReworkWithAIButton"
import { AIMessageConfiguration } from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/AI/AIMessageConfiguration"
import MessageSettingsForm from "@/pages/campaign-form/components/sections/basics/AdvancedTextSettings/MessageSettingsForm/MessageSettingsForm"

const { DraftOptions } = DjangIO.app.grassroots.campaign.types

const DRAFT_OPTIONS_LABEL_MAPPER = {
    [DraftOptions.can_edit.value]: "Full",
    [DraftOptions.partial_edit.value]: "Partial",
    [DraftOptions.cannot_edit.value]: "None",
}

export const PERSONALIZED_PAGES_KEY = "personalized_campaign_pages"

const reworkWithAIEnabledFF = isFeatureEnabled("ff_ngg_rework_interactive_by_ai")

export const Messaging = () => {
    const formContext = useFormContext()

    const [isSavedQuestionsModalOpen, savedQuestionsModalActions] = useDisclosure(false)
    const [createQuestionOpened, createQuestionActions] = useDisclosure(false)

    const hasCustomTarget = formContext.watch("message_groups[0].has_custom_email_targets")
    const isPartialEditActive =
        formContext.watch("message_groups[0].advocate_editing_permission") === DraftOptions.partial_edit.value

    const reworkWithAIEnabled = formContext.watch("should_rework_with_ai")

    const personalizedPages = formContext.getValues(PERSONALIZED_PAGES_KEY)

    const placeholders = useCampaignPlaceholders()
    const availablePlaceholders = placeholders?.data

    return (
        <>
            <TwoColumnsLayout columnWidth={540}>
                <div>
                    {!isCustomTargetingEnabled(formContext) && (
                        <Radio.Group
                            name="message_groups[0].has_custom_email_targets"
                            label="Choose recipients"
                            transform={{
                                input: (input: boolean) => String(input),
                                output: (output: string) => (output === "true" ? true : false),
                            }}
                        >
                            <Group>
                                <Radio value={"false"} label="Officials" />
                                <Radio value={"true"} label="Custom recipients" />
                            </Group>
                        </Radio.Group>
                    )}
                    {hasCustomTarget ? (
                        <AddTextItemsToListInput
                            name="message_groups[0].custom_email_targets"
                            label="Enter email address (10 maximum)"
                            placeholder="Enter email address here"
                            maxItems={10}
                            rules={{
                                required: "Add at least one email",
                            }}
                            transform={{
                                output: (output: string) => output.toLowerCase(),
                            }}
                            filters={{
                                inputValidate: emailValidation,
                            }}
                        />
                    ) : (
                        <TargetedOfficialsSelectionWriteLetter messageIndex={0} prefix={null} />
                    )}
                    {reworkWithAIEnabledFF && <MessageSettingsForm />}
                </div>
                <div className={classes.box}>
                    {reworkWithAIEnabledFF && <ReworkWithAIButton />}
                    <CustomQuestions
                        onPlusClick={createQuestionActions.open}
                        onTaskClick={savedQuestionsModalActions.open}
                    />

                    <Radio.Group
                        name="message_groups[0].advocate_editing_permission"
                        label="Advocate editing permissions"
                        transform={{
                            input: (input: string | number) => Number(input),
                            output: (output: string) => Number(output),
                        }}
                        onChange={() => formContext.clearErrors()}
                    >
                        <Group spacing="sm">
                            {DraftOptions.items()
                                .sort(enumOrderSort)
                                .map((option) => {
                                    const label = DRAFT_OPTIONS_LABEL_MAPPER[option.value]
                                    return <Radio value={option.value} label={label} />
                                })}
                        </Group>
                    </Radio.Group>

                    <TextInputWithPlaceholders
                        name="message_groups[0].message_variations[0].subject"
                        rules={{
                            required: "Subject is required.",
                            validate: validatePlaceholders,
                        }}
                        dataCy="message-subject"
                        label="Subject"
                        placeholder="Enter subject"
                        placeholders={availablePlaceholders}
                        removeTargetPlaceholders={hasCustomTarget}
                    />
                    {isPartialEditActive && (
                        <TextAreaWithPlaceholders
                            label="Fixed pre-message"
                            name="message_groups[0].message_variations[0].fixed_pre_content"
                            placeholders={availablePlaceholders}
                            rules={{
                                validate: validatePlaceholders,
                            }}
                            removeTargetPlaceholders={hasCustomTarget}
                        />
                    )}

                    <TextAreaWithPlaceholders
                        name="message_groups[0].message_variations[0].content"
                        label="Message"
                        dataCy="message-body"
                        placeholder="Enter message"
                        placeholders={availablePlaceholders}
                        rules={{
                            required: !isPartialEditActive ? "Message cannot be empty." : false,
                            validate: validatePlaceholders,
                        }}
                        removeTargetPlaceholders={hasCustomTarget}
                        showPreviewButton={reworkWithAIEnabled}
                    />

                    {isPartialEditActive && (
                        <TextAreaWithPlaceholders
                            label="Fixed post-message"
                            name="message_groups[0].message_variations[0].fixed_post_content"
                            placeholders={availablePlaceholders}
                            rules={{
                                validate: validatePlaceholders,
                            }}
                            removeTargetPlaceholders={hasCustomTarget}
                        />
                    )}
                    {reworkWithAIEnabledFF && reworkWithAIEnabled && <AIMessageConfiguration />}
                </div>
            </TwoColumnsLayout>
            <CreateQuestionModal
                opened={createQuestionOpened}
                onClose={createQuestionActions.close}
                onSuccessCallback={(customField) => {
                    if (!personalizedPages?.[0]) formContext.setValue(PERSONALIZED_PAGES_KEY, { 0: [customField] })
                    else
                        formContext.setValue(PERSONALIZED_PAGES_KEY, {
                            0: [...personalizedPages[0], customField],
                        })
                }}
            />
            <SavedQuestionsModal
                opened={isSavedQuestionsModalOpen}
                onClose={savedQuestionsModalActions.close}
                addQuestion={createQuestionActions.open}
            />
        </>
    )
}
